// Écran de résultat du chemin modèle : un aliment par ligne, un poids EN FOURCHETTE, un curseur
// d'ajustement, et des macros qui se recalculent en direct — localement, sans aucun aller-retour
// serveur (le calcul est une règle de trois, cf. result/portions.ts).
//
// Le rendu se fait en DOM (`createElement` + `textContent`), jamais par `innerHTML` interpolé : le
// nom d'un aliment vient du modèle, donc d'un texte non maîtrisé, au même titre qu'une fiche Open
// Food Facts (web/src/htmlSafety.ts § pourquoi).
import type { AlimentDetecte, AnalyzeDone } from '../analyze/types.js';
import type { MacrosPer100g } from '../barcode/types.js';
import { ALIMENTS_COURANTS, alimentCourant } from '../result/alimentsCourants.js';
import {
  ajouter,
  ajusterPoids,
  alimentDepuisDetection,
  alimentManuel,
  bornesCurseur,
  confianceBasse,
  fourchettePlat,
  macrosCourantes,
  renommer,
  supprimer,
  totauxPlat,
  type AlimentAjustable,
} from '../result/portions.js';

/** Pause d'inactivité avant de remonter une portion corrigée (voir le `change` du curseur). */
const DELAI_CORRECTION_MS = 800;

export interface CorrectionPortion {
  aliment: string;
  estimationModele: { poids_g_min: number; poids_g_max: number };
  valeurCorrigeeG: number;
  origine: 'curseur' | 'saisie';
}

/** Un aliment au poids retenu ET ses macros à l'échelle — ce que POST /api/journal attend. */
export interface AlimentPourJournal {
  nom: string;
  poidsG: number;
  macros: MacrosPer100g;
}

export interface VueResultat {
  readonly element: HTMLElement;
  /** Vide la liste : évènement `reprise` (tout ce qui a été affiché avant est caduc). */
  reinitialiser(): void;
  ajouterAlimentDetecte(detecte: AlimentDetecte): void;
  terminer(done: AnalyzeDone): void;
  /** Nombre d'aliments actuellement affichés (le premier arrive avant la fin de l'analyse). */
  readonly nombreAliments: number;
}

export function creerVueResultat(options: {
  surCorrection?: (correction: CorrectionPortion) => void;
  /** Bouton « Enregistrer ce repas » (rendreTotaux) : absent = fonctionnalité masquée. Résout/rejette
   *  pour piloter le message affiché sous le bouton. */
  surEnregistrerRepas?: (aliments: AlimentPourJournal[]) => Promise<void>;
} = {}): VueResultat {
  const element = document.createElement('section');
  element.className = 'resultat';
  element.hidden = true;

  const liste = document.createElement('ul');
  liste.className = 'aliments';
  liste.setAttribute('aria-label', 'Aliments détectés');

  const totauxEl = document.createElement('div');
  totauxEl.className = 'totaux';
  totauxEl.setAttribute('role', 'status');
  totauxEl.setAttribute('aria-live', 'polite');

  const ajoutEl = creerBlocAjout((aliment) => {
    aliments = ajouter(aliments, aliment);
    liste.append(creerLigne(aliment));
    rendreTotaux();
  });

  const journalEl = creerBlocJournal(() => aliments, options.surEnregistrerRepas);

  element.append(liste, totauxEl, journalEl.element, ajoutEl);

  let aliments: AlimentAjustable[] = [];
  let compteur = 0;
  let done: AnalyzeDone | null = null;

  function rendreTotaux(): void {
    totauxEl.replaceChildren();
    journalEl.majDisponibilite(aliments.length > 0);
    if (aliments.length === 0) {
      if (done) totauxEl.append(texte('p', 'Aucun aliment retenu sur cette photo.'));
      return;
    }

    const totaux = totauxPlat(aliments);
    const fourchette = fourchettePlat(aliments);

    const titre = document.createElement('h2');
    titre.textContent = 'Total du repas';
    const poids = document.createElement('p');
    poids.className = 'totaux-poids';
    poids.textContent =
      fourchette && fourchette.poids_g_min !== fourchette.poids_g_max
        ? `${fourchette.poids_g_min}–${fourchette.poids_g_max} g estimés · ${totaux.poidsG} g retenus`
        : `${totaux.poidsG} g`;

    const kcal = document.createElement('p');
    kcal.className = 'totaux-kcal';
    kcal.textContent = `${totaux.macros.kcal} kcal`;

    totauxEl.append(titre, poids, kcal, listeMacros(totaux.macros));

    if (totaux.alimentsSansComposition > 0) {
      totauxEl.append(
        texte(
          'p',
          `${totaux.alimentsSansComposition} aliment(s) sans composition connue : comptés dans le poids, pas dans les macros.`,
          'note',
        ),
      );
    }

    if (done) {
      // La calibration post-hoc a été ajustée sur le poids TOTAL d'une assiette et n'est pas
      // additive : elle ne s'affiche qu'ici, jamais aliment par aliment (api/src/types.ts).
      const facteur = done.totauxCalibres.facteur;
      totauxEl.append(
        texte(
          'p',
          `Corrigé de l'écart mesuré sur le jeu d'évaluation (×${facteur.toFixed(2)}) : ` +
            `${done.totauxCalibres.poids_g_min}–${done.totauxCalibres.poids_g_max} g, ` +
            `${done.totauxCalibres.kcal} kcal.`,
          'note',
        ),
      );
    }
  }

  function creerLigne(aliment: AlimentAjustable): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'aliment';
    if (confianceBasse(aliment)) li.classList.add('confiance-basse');
    li.dataset.id = aliment.id;

    const tete = document.createElement('div');
    tete.className = 'aliment-tete';

    const nom = document.createElement('input');
    nom.type = 'text';
    nom.className = 'aliment-nom';
    nom.value = aliment.nom;
    nom.setAttribute('aria-label', "Nom de l'aliment");
    nom.addEventListener('change', () => {
      aliments = renommer(aliments, aliment.id, nom.value);
      const aJour = aliments.find((a) => a.id === aliment.id);
      if (aJour) {
        nom.value = aJour.nom;
        supprimerBtn.setAttribute('aria-label', `Supprimer ${aJour.nom}`);
      }
    });

    const supprimerBtn = document.createElement('button');
    supprimerBtn.type = 'button';
    supprimerBtn.className = 'aliment-supprimer';
    supprimerBtn.textContent = '✕';
    supprimerBtn.setAttribute('aria-label', `Supprimer ${aliment.nom}`);
    supprimerBtn.addEventListener('click', () => {
      aliments = supprimer(aliments, aliment.id);
      li.remove();
      rendreTotaux();
    });

    tete.append(nom, supprimerBtn);

    const estimation = document.createElement('p');
    estimation.className = 'aliment-estimation';
    estimation.textContent = aliment.estimation
      ? `Estimé ${aliment.estimation.poids_g_min}–${aliment.estimation.poids_g_max} g` +
        (confianceBasse(aliment) ? ' · confiance faible, à vérifier' : '')
      : 'Ajouté à la main';

    const bornes = bornesCurseur(aliment);
    const curseurId = `poids-${aliment.id}`;

    const label = document.createElement('label');
    label.className = 'aliment-poids';
    label.htmlFor = curseurId;
    const valeur = document.createElement('span');
    valeur.className = 'aliment-poids-valeur';
    valeur.textContent = `${aliment.poidsG} g`;
    label.append(document.createTextNode('Poids retenu : '), valeur);

    const curseur = document.createElement('input');
    curseur.type = 'range';
    curseur.id = curseurId;
    curseur.className = 'aliment-curseur';
    curseur.min = String(bornes.min);
    curseur.max = String(bornes.max);
    curseur.step = String(bornes.pas);
    curseur.value = String(aliment.poidsG);

    const macrosEl = document.createElement('p');
    macrosEl.className = 'aliment-macros';

    const rendreLigne = (): void => {
      const courant = aliments.find((a) => a.id === aliment.id);
      if (!courant) return;
      valeur.textContent = `${courant.poidsG} g`;
      const macros = macrosCourantes(courant);
      macrosEl.textContent = macros
        ? `${macros.kcal} kcal · ${macros.glucides_g} g gluc. · ${macros.proteines_g} g prot. · ${macros.lipides_g} g lip.`
        : 'Composition inconnue';
    };

    // `input` : recalcul local à chaque pixel, zéro requête (critère d'acceptation).
    curseur.addEventListener('input', () => {
      aliments = ajusterPoids(aliments, aliment.id, Number(curseur.value));
      rendreLigne();
      rendreTotaux();
    });
    // `change` : le doigt a quitté le curseur — c'est là, et seulement là, qu'on remonte la
    // correction (POST /api/correction), pas à chaque pixel. Et même là, on attend une pause :
    // au clavier, chaque flèche émet un `change`, et chaque valeur intermédiaire partirait dans
    // le jeu d'évaluation comme si c'était une vérité terrain. Seule la valeur sur laquelle
    // l'utilisateur s'est arrêté en est une.
    let attente: ReturnType<typeof setTimeout> | undefined;
    curseur.addEventListener('change', () => {
      clearTimeout(attente);
      attente = setTimeout(() => {
        const courant = aliments.find((a) => a.id === aliment.id);
        if (!courant?.estimation) return;
        options.surCorrection?.({
          aliment: courant.nom,
          estimationModele: courant.estimation,
          valeurCorrigeeG: courant.poidsG,
          origine: 'curseur',
        });
      }, DELAI_CORRECTION_MS);
    });

    li.append(tete, estimation, label, curseur, macrosEl);
    rendreLigne();
    return li;
  }

  return {
    element,
    get nombreAliments() {
      return aliments.length;
    },
    reinitialiser() {
      aliments = [];
      done = null;
      liste.replaceChildren();
      rendreTotaux();
    },
    ajouterAlimentDetecte(detecte) {
      element.hidden = false;
      compteur += 1;
      const aliment = alimentDepuisDetection(detecte, `a${compteur}`);
      aliments = ajouter(aliments, aliment);
      liste.append(creerLigne(aliment));
      rendreTotaux();
    },
    terminer(fin) {
      element.hidden = false;
      done = fin;
      rendreTotaux();
    },
  };
}

function creerBlocAjout(surAjout: (aliment: AlimentAjustable) => void): HTMLElement {
  let compteurManuel = 0;

  const details = document.createElement('details');
  details.className = 'ajout-manuel';
  const resume = document.createElement('summary');
  resume.textContent = 'Ajouter un aliment oublié';
  details.append(resume);

  const select = document.createElement('select');
  select.id = 'ajout-aliment';
  for (const courant of ALIMENTS_COURANTS) {
    const option = document.createElement('option');
    option.value = courant.cle;
    option.textContent = courant.nom;
    select.append(option);
  }
  const labelSelect = document.createElement('label');
  labelSelect.htmlFor = select.id;
  labelSelect.textContent = 'Aliment';

  const poids = document.createElement('input');
  poids.type = 'number';
  poids.id = 'ajout-poids';
  poids.min = '1';
  poids.step = '5';
  poids.inputMode = 'numeric';
  poids.value = String(ALIMENTS_COURANTS[0]?.portionG ?? 100);
  const labelPoids = document.createElement('label');
  labelPoids.htmlFor = poids.id;
  labelPoids.textContent = 'Poids (g)';

  select.addEventListener('change', () => {
    const courant = alimentCourant(select.value);
    if (courant) poids.value = String(courant.portionG);
  });

  const bouton = document.createElement('button');
  bouton.type = 'button';
  bouton.className = 'primaire';
  bouton.textContent = 'Ajouter';
  bouton.addEventListener('click', () => {
    const courant = alimentCourant(select.value);
    if (!courant) return;
    const grammes = Number(poids.value);
    compteurManuel += 1;
    surAjout(
      alimentManuel(
        `m${compteurManuel}`,
        courant.macrosPour100g ? courant.nom : 'Aliment ajouté',
        Number.isFinite(grammes) && grammes > 0 ? grammes : courant.portionG,
        courant.macrosPour100g,
      ),
    );
  });

  const note = texte(
    'p',
    'Valeurs de référence pour 100 g, à ajuster : elles dépannent, elles ne remplacent pas une fiche produit.',
    'note',
  );

  details.append(labelSelect, select, labelPoids, poids, bouton, note);
  return details;
}

interface BlocJournal {
  readonly element: HTMLElement;
  /** Affiche/masque le bouton selon qu'il y a quelque chose à enregistrer (rendreTotaux). */
  majDisponibilite(disponible: boolean): void;
}

/**
 * Bouton « Enregistrer ce repas » (CLAUDE.md § Journal alimentaire) : POST /api/journal avec les
 * aliments AU POIDS ET AUX MACROS COURANTS (curseur compris) — jamais les valeurs initiales du
 * modèle, l'utilisateur a pu corriger avant d'enregistrer. Masqué entièrement si `surEnregistrer`
 * n'est pas fourni (main.ts en décide) : cet écran de résultat reste utilisable sans journal.
 */
function creerBlocJournal(
  obtenirAliments: () => AlimentAjustable[],
  surEnregistrer?: (aliments: AlimentPourJournal[]) => Promise<void>,
): BlocJournal {
  const element = document.createElement('div');
  element.className = 'bloc-journal';
  if (!surEnregistrer) {
    element.hidden = true;
    return { element, majDisponibilite() {} };
  }

  const bouton = document.createElement('button');
  bouton.type = 'button';
  bouton.className = 'primaire bouton-journal';
  bouton.textContent = 'Enregistrer ce repas';

  const messageEl = document.createElement('p');
  messageEl.className = 'journal-message';
  messageEl.setAttribute('role', 'status');
  messageEl.setAttribute('aria-live', 'polite');

  bouton.addEventListener('click', () => {
    const pourJournal: AlimentPourJournal[] = obtenirAliments()
      .map((aliment) => ({ nom: aliment.nom, poidsG: aliment.poidsG, macros: macrosCourantes(aliment) }))
      .filter((a): a is AlimentPourJournal => a.macros !== null);

    if (pourJournal.length === 0) {
      messageEl.textContent = 'Composition inconnue : impossible d’enregistrer ce repas.';
      return;
    }

    bouton.disabled = true;
    messageEl.textContent = 'Enregistrement…';
    surEnregistrer(pourJournal)
      .then(() => {
        messageEl.textContent = 'Enregistré dans le journal ✓';
      })
      .catch(() => {
        messageEl.textContent = "L'enregistrement a échoué. Réessaie dans un instant.";
        bouton.disabled = false;
      });
  });

  element.append(bouton, messageEl);
  return {
    element,
    majDisponibilite(disponible) {
      element.hidden = !disponible;
      if (disponible) {
        bouton.disabled = false;
        messageEl.textContent = '';
      }
    },
  };
}

function listeMacros(macros: MacrosPer100g): HTMLUListElement {
  const ul = document.createElement('ul');
  ul.className = 'macros';
  const lignes: [string, string][] = [
    ['Glucides', `${macros.glucides_g} g`],
    ['dont sucres', `${macros.sucres_g} g`],
    ['Protéines', `${macros.proteines_g} g`],
    ['Lipides', `${macros.lipides_g} g`],
    ['Fibres', `${macros.fibres_g} g`],
    ['Sel', `${macros.sel_g} g`],
  ];
  for (const [nom, valeur] of lignes) {
    const li = document.createElement('li');
    li.textContent = `${nom} : ${valeur}`;
    ul.append(li);
  }
  return ul;
}

function texte(balise: keyof HTMLElementTagNameMap, contenu: string, classe?: string): HTMLElement {
  const el = document.createElement(balise);
  el.textContent = contenu;
  if (classe) el.className = classe;
  return el;
}
